import findOptionStock from "$lib/utils/findOptionStock";
import { CurrencyCode } from "$lib/gql/gen/codegen";
import type { Product } from "$lib/gql/gen/codegen";
import { idFromShopify } from "./routes";
import type { BaseCartLineEdge } from "../gql/gen/codegen";

/** pushGTMEvent - Pushes an event object to GTM dataLayer.
 * @param {string} [name] - Name of event.
 * @param {Object} [properties={}] - Any custom properties to set on the event.
 * @param {Object} [globalObject=window] – E.g. a reference to the Window Object (window). */
export function pushGtmEvent(
  name: string,
  properties = {},
  globalObject = window,
) {
  const eventObject = { ...properties };
  if (name) eventObject["event"] = name;
  if (globalObject["dataLayer"]) {
    globalObject["dataLayer"].push(eventObject);
  } else {
    globalObject["dataLayer"] = [eventObject];
  }
}

/** pushGtmEcommerceEvent - Pushes a GA4 Ecommerce event object to GTM dataLayer.
 * @param {string} name - Name of event.
 * @param {Object} basketItems - Value of the lines.edges of the 'cart' store
 * @param {Object} [globalObject=window] – E.g. a reference to the Window Object (window). */
export function pushGtmEcommerceEvent(
  name: string,
  basketItems: Array<BaseCartLineEdge>,
  globalObject = window,
) {
  if (!name)
    throw new Error("An event name must be supplied for ecommerce events");
  if (!basketItems)
    throw new Error("A basket object must be supplied for ecommerce events");
  pushGtmEvent(null, { ecommerce: null }, globalObject);
  pushGtmEvent(
    name,
    {
      ecommerce: {
        currencyCode: getCurrencyCodeFromBasketItems(basketItems),
        items: getEcommerceEventItemsFromBasketItems(basketItems),
      },
    },
    globalObject,
  );
}

/** getCurrencyCodeFromBasketItems - Extracts the active currency code from an instance of the `cart` store.
 * @param {Object} basketItems - Value of the lines.edges of the 'cart' store
 * @returns {string} - Currency code. */
export function getCurrencyCodeFromBasketItems(
  basketItems: Array<BaseCartLineEdge>,
) {
  if (!basketItems) throw new Error("A basket value must be supplied");
  if (!basketItems[0]?.node?.merchandise?.price) return CurrencyCode.Gbp;
  return basketItems[0].node.merchandise.price.currencyCode;
}

/** getEcommerceEventItemsFromBasketItems - Transforms .
 * @param {Object} basketItems - Value of the lines.edges of the 'cart' store
 * @returns {array} - Array of objects containing GA4 Ecommerce-style representation of cart items. */
export function getEcommerceEventItemsFromBasketItems(
  basketItems: Array<BaseCartLineEdge>,
) {
  if (!basketItems) throw new Error("A basket value must be supplied");
  return basketItems.map((item: any) => ({
    item_name: item?.node?.merchandise?.product?.title,
    item_id: idFromShopify(item?.node?.merchandise?.id),
    skuCode: item?.node?.merchandise?.sku,
    item_variant: item?.node?.merchandise.sku.includes("GIFT CARD")
      ? item?.node?.merchandise?.selectedOptions?.find(
          opt => opt.name === "Denominations",
        ).value
      : item?.node?.merchandise?.selectedOptions?.find(
          opt => opt.name === "colour",
        ).value,
    price: parseFloat(item?.node?.merchandise?.price?.amount),
    quantity: item?.node?.quantity,
    sale_status: item?.node?.merchandise?.compareAtPrice ? "true" : "false",
  }));
}

/** pushGtmEcommerceEventForProduct - Pushes a GA4 Ecommerce event object to GTM dataLayer.
 * @param {string} name - Name of event.
 * @param {Object} productData - Instance of the `product` data.
 * @param {Object} [globalObject=window] – E.g. a reference to the Window Object (window). */
export function pushGtmEcommerceEventForProduct(
  name: string,
  productData: Product,
  globalObject = window,
) {
  if (!name)
    throw new Error("An event name must be supplied for ecommerce events");
  if (!productData)
    throw new Error("A product object must be supplied for ecommerce events");
  pushGtmEvent(null, { ecommerce: null }, globalObject);
  pushGtmEvent(
    name,
    {
      ecommerce: {
        currencyCode: productData.priceRange.maxVariantPrice.currencyCode,
        items: getEcommerceEventFromProduct(productData),
      },
    },
    globalObject,
  );
}

/** getEcommerceEventFromProduct - Transforms .
 * @param {Object} productData - Instance of the `products` data.
 * @returns {array} - Array of objects containing GA4 Ecommerce-style representation of a product. */
export function getEcommerceEventFromProduct(productData: any) {
  if (!productData) throw new Error("A product value must be supplied");
  const productColours = productData.options?.find(
    opt => opt.name === "colour",
  );
  return {
    item_name: productData.title,
    item_id: idFromShopify(productData.id),
    price: parseFloat(productData.priceRange.maxVariantPrice.amount),
    item_category:
      productData.collections && productData.collections.edges[0].node.title,
    item_variant:
      productColours && productData.variants?.edges
        ? findOptionStock("colour", productData.variants.edges).map(
            optStk => optStk.value,
          )[0]
        : productColours?.values[0],
    quantity: 1,
  };
}

/** pushCustomGtmEventData - Pushes a custom ecommerce event object to GTM dataLayer.
 * @param {string} name - Name of event.
 * @param {Object} event - Instance of the event object.
 * @param {Object} [globalObject=window] – E.g. a reference to the Window Object (window). */
export function pushCustomGtmEventData(
  name: string,
  event: any,
  globalObject = window,
) {
  if (!name)
    throw new Error("An event name must be supplied for custom events");
  if (!event)
    throw new Error("An event object must be supplied for custom events");
  pushGtmEvent(
    name,
    {
      eventData: {
        event,
      },
    },
    globalObject,
  );
}
